import React from 'react'
import { ScrollView, Text, View } from 'react-native'
import { BrandMark } from '@warcarp/ui'
import { LogoEggFrame } from '../../../components/home/logoEgg/LogoEggStage'
import { EnemySprite } from '../../../components/home/logoEgg/LogoEggSprites'
import { useTheme } from '../../../hooks/useTheme'
import { LOGO_ENEMIES, LOGO_REACTION_LABELS, type LogoEnemy, type LogoReaction } from '../../../lib/logoEasterEgg'
import { ENEMY_ART, FISH_ASPECT } from '../../../lib/logoEggArt'
import { LOGO_SCENE_DURATIONS, shotTier } from '../../../lib/logoEggScene'

const BOX = { width: 108, height: 82 }
const LOGO = { x: 6, y: 5, height: 17 }
/** The first seed whose shots land in the upper band (seed 0 is always mid). */
const UPPER_SEED = Array.from({ length: 64 }, (_, n) => n + 1).find(n => shotTier(n) === 'upper')!
const LAYOUT = { stage: BOX, fish: { x: LOGO.x, y: LOGO.y, width: LOGO.height * FISH_ASPECT, height: LOGO.height } }

/** Key moments (ms) of each reaction; frames are fixed, so this page is deterministic. */
const STRIPS: { title: string; reaction: LogoReaction; enemies?: LogoEnemy[]; times: number[]; reducedMotion?: boolean; seed?: number }[] = [
  // Fire at each character: entrance, its behaviour as the carp takes aim, its own explosion.
  { title: LOGO_REACTION_LABELS['fire-puffer'], reaction: 'fire-puffer', times: [700, 1140, 1400] },
  { title: LOGO_REACTION_LABELS['fire-jelly'], reaction: 'fire-jelly', times: [700, 1140, 1450] },
  { title: LOGO_REACTION_LABELS['fire-shark'], reaction: 'fire-shark', times: [700, 1140, 1400] },
  { title: LOGO_REACTION_LABELS['fire-angler'], reaction: 'fire-angler', times: [700, 1140, 1650] },
  { title: LOGO_REACTION_LABELS['fire-crab'], reaction: 'fire-crab', times: [700, 1140, 1500] },
  { title: LOGO_REACTION_LABELS['fire-squid'], reaction: 'fire-squid', times: [500, 1140, 1500] },
  { title: LOGO_REACTION_LABELS['fire-moray'], reaction: 'fire-moray', times: [600, 1140, 1450] },
  { title: LOGO_REACTION_LABELS['fire-urchin'], reaction: 'fire-urchin', times: [500, 1140, 1400] },
  { title: LOGO_REACTION_LABELS['fire-seahorse'], reaction: 'fire-seahorse', times: [500, 1140, 1500] },
  // Some plays land high, just under the header line.
  { title: `${LOGO_REACTION_LABELS['fire-shark']} · upper band`, reaction: 'fire-shark', seed: UPPER_SEED, times: [1140, 1400] },
  { title: LOGO_REACTION_LABELS['fire-double'], reaction: 'fire-double', times: [1000, 1300, 2150] },
  { title: LOGO_REACTION_LABELS['fire-miss'], reaction: 'fire-miss', times: [1080, 1700, 2150] },
  // One round, three hits, then the combo chip; a second seed shows the spots vary from play to play.
  { title: LOGO_REACTION_LABELS['fire-combo'], reaction: 'fire-combo', times: [1660, 1960, 2350] },
  { title: `${LOGO_REACTION_LABELS['fire-combo']} · another play`, reaction: 'fire-combo', seed: 7, times: [1450, 1900, 2350] },
  { title: LOGO_REACTION_LABELS.wink, reaction: 'wink', times: [300, 650, 1050] },
  { title: LOGO_REACTION_LABELS.flip, reaction: 'flip', times: [420, 700, 1150] },
  { title: LOGO_REACTION_LABELS.swim, reaction: 'swim', times: [1100, 2200, 3300] },
  { title: LOGO_REACTION_LABELS.bubbles, reaction: 'bubbles', times: [500, 1000, 1500] },
  { title: LOGO_REACTION_LABELS.salute, reaction: 'salute', times: [300, 800, 1250] },
  { title: LOGO_REACTION_LABELS.shake, reaction: 'shake', times: [350, 560, 1100] },
  { title: LOGO_REACTION_LABELS.war, reaction: 'war', times: [300, 800, 1400] },
  { title: LOGO_REACTION_LABELS.chase, reaction: 'chase', times: [700, 1100, 1650] },
  { title: LOGO_REACTION_LABELS.invasion, reaction: 'invasion', enemies: ['puffer', 'jelly', 'crab'], times: [1000, 1450, 2750] },
  { title: LOGO_REACTION_LABELS.dance, reaction: 'dance', times: [1000, 1700, 2100] },
  { title: LOGO_REACTION_LABELS.hula, reaction: 'hula', times: [400, 1100, 1300] },
  { title: LOGO_REACTION_LABELS.juggle, reaction: 'juggle', times: [800, 1200, 2280] },
  { title: LOGO_REACTION_LABELS.magic, reaction: 'magic', times: [850, 1260, 1750] },
  { title: LOGO_REACTION_LABELS.skate, reaction: 'skate', times: [600, 960, 1650] },
  { title: LOGO_REACTION_LABELS.sneeze, reaction: 'sneeze', times: [950, 1250, 1750] },
  { title: LOGO_REACTION_LABELS.duel, reaction: 'duel', times: [700, 1600, 1950] },
  { title: LOGO_REACTION_LABELS.waltz, reaction: 'waltz', times: [1050, 1800, 2600] },
  { title: LOGO_REACTION_LABELS.rocket, reaction: 'rocket', times: [700, 1400, 2250] },
  { title: LOGO_REACTION_LABELS.balloon, reaction: 'balloon', times: [1100, 1450, 2050] },
  { title: LOGO_REACTION_LABELS.disguise, reaction: 'disguise', times: [600, 1200, 1900] },
  { title: LOGO_REACTION_LABELS.lift, reaction: 'lift', times: [1000, 1450, 1850] },
  { title: LOGO_REACTION_LABELS.paperclip, reaction: 'paperclip', times: [1200, 2350, 2700] },
  { title: 'Reduced motion', reaction: 'wink', reducedMotion: true, times: [550] },
]

function Frame({ reaction, enemies, ms, variant, reducedMotion, seed }: { reaction: LogoReaction; enemies?: LogoEnemy[]; ms: number; variant: 'light' | 'dark'; reducedMotion?: boolean; seed?: number }) {
  const duration = reducedMotion ? 1100 : LOGO_SCENE_DURATIONS[reaction]
  return <View className="bg-canvas border border-olive" style={{ ...BOX, overflow: 'hidden' }}>
    <View className="bg-paper border-b border-olive" style={{ height: LOGO.y * 2 + LOGO.height }} />
    <View style={{ position: 'absolute', left: LOGO.x, top: LOGO.y }}><BrandMark kind="logo" height={LOGO.height} variant={variant} hideFish /></View>
    <LogoEggFrame reaction={reaction} enemies={enemies} layout={LAYOUT} frame={ms / duration} variant={variant} reducedMotion={reducedMotion} seed={seed} />
    <Text className="font-typed text-ink-soft" style={{ position: 'absolute', right: 3, bottom: 1, fontSize: 9 }}>{(ms / 1000).toFixed(2)}s</Text>
  </View>
}

/**
 * Static film strips of every logo easter egg reaction and the nine enemy
 * characters, for review and the `logo-easter-egg` visual baseline. The live
 * version is /preview/logo-easter-egg.
 */
export default function LogoEasterEggFrames() {
  const { theme } = useTheme()
  const variant = theme === 'dark' ? 'dark' : 'light'
  return <ScrollView className="flex-1 bg-canvas" contentContainerStyle={{ padding: 8, gap: 8 }} testID="logo-egg-frames">
    <Text className="font-stencil text-ink text-sm uppercase">Logo easter egg · the nine invaders</Text>
    <View className="flex-row flex-wrap" style={{ gap: 8 }}>
      {LOGO_ENEMIES.map(enemy => {
        const art = ENEMY_ART[enemy]
        return <View key={enemy} className="bg-paper border border-olive rounded-lg p-2 items-center" style={{ width: 112, gap: 4 }}>
          <View style={{ height: 66, justifyContent: 'center' }}><EnemySprite enemy={enemy} width={art.grid[0]!.length * 5} height={art.grid.length * 5} /></View>
          <Text className="font-stencil text-ink text-xs uppercase">{art.name}</Text>
          <View className="flex-row" style={{ gap: 3 }}>{art.burst.map(colour => <View key={colour} style={{ width: 10, height: 10, backgroundColor: colour }} />)}</View>
        </View>
      })}
    </View>
    <View className="flex-row flex-wrap" style={{ gap: 8 }}>
      {STRIPS.map(strip => <View key={strip.title} className="bg-paper border border-olive rounded-lg p-2" style={{ gap: 4 }}>
        <Text className="font-stencil text-ink text-xs uppercase">{strip.title}</Text>
        <View className="flex-row flex-wrap" style={{ gap: 4, maxWidth: BOX.width * 3 + 8 }}>
          {strip.times.map((ms, n) => <Frame key={n} reaction={strip.reaction} ms={ms} variant={variant} reducedMotion={strip.reducedMotion}
            enemies={strip.enemies} seed={strip.seed} />)}
        </View>
      </View>)}
    </View>
  </ScrollView>
}
